import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LogIn, ShieldCheck, Mail, Lock, User as UserIcon, Loader2 } from 'lucide-react';
import { usePasswordReveal } from '../components/PasswordReveal';
import { useAuth } from '../context/AuthContext';
import { EmailHint } from '../components/EmailHealth';

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1.1 7.3 2.8l5.7-5.7C33.6 6.1 29.1 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c2.8 0 5.4 1.1 7.3 2.8l5.7-5.7C33.6 6.1 29.1 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.6-3.6-11.2-8.4l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.6l6.2 5.2C40.9 35.6 44 30.3 44 24c0-1.3-.1-2.3-.4-3.5z"/>
    </svg>
  );
}

const inputWrap = 'flex items-center gap-2 border rounded px-3 py-2.5 bg-white';
const inputCls = 'w-full outline-none text-sm bg-transparent';

export default function Login() {
  const { loginWithGoogle, login, register, forgotPassword } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // login | register | forgot
  const [email, setEmail] = useState('');
  const [emailOk, setEmailOk] = useState(true);
  const [password, setPassword] = useState('');
  const [pwType, pwEye] = usePasswordReveal('auth-password-reveal');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(location.state?.error || '');
  const [notice, setNotice] = useState('');

  const nextParam = new URLSearchParams(location.search).get('next') || '';
  const nextPath = nextParam.startsWith('/') && !nextParam.startsWith('//') ? nextParam : '/account';
  const reset = () => { setError(''); setNotice(''); };

  const submit = async (e) => {
    e.preventDefault();
    reset();
    setBusy(true);
    try {
      if (mode === 'login') {
        const u = await login(email.trim(), password);
        navigate(nextParam ? nextPath : (['committee', 'admin'].includes(u?.memberClass) ? '/admin/ops' : nextPath));
      } else if (mode === 'register') {
        await register(email.trim(), password, name.trim());
        navigate(nextPath);
      } else if (mode === 'forgot') {
        await forgotPassword(email.trim());
        setNotice('If an account exists for that email, a password reset link has been sent. Check your inbox.');
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && typeof d === 'object' && d.code === 'setup_required') { setError(''); setNotice(d.message); }
      else setError(formatApiErrorDetail(d) || err.message);
    }
    setBusy(false);
  };

  const titles = {
    login: 'Member Login',
    register: 'Create your account',
    forgot: 'Reset your password',
  };
  const subtitles = {
    login: 'Sign in with your email and password, or continue with Google.',
    register: 'Register with an email and password. New members start at Contact level.',
    forgot: "Enter your email and we'll send you a reset link.",
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16 fade-in">
      <div className="bg-white rounded-lg shadow-md p-8 border" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
        <div className="text-center mb-6">
          <div className="mx-auto mb-4 flex items-center justify-center w-14 h-14 rounded-full" style={{ background: 'var(--navy)' }}>
            <LogIn size={24} color="var(--cream)" />
          </div>
          <h1 className="font-title text-2xl" style={{ color: 'var(--maroon)' }} data-testid="login-title">{titles[mode]}</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--olive)' }}>{subtitles[mode]}</p>
        </div>

        {error && (
          <p className="text-sm text-center mb-4 px-3 py-2 rounded" style={{ background: '#fdecea', color: '#8a1f11' }} data-testid="login-error">
            {error}
          </p>
        )}
        {notice && (
          <p className="text-sm text-center mb-4 px-3 py-2 rounded" style={{ background: '#eaf6ec', color: '#1c6b2c' }} data-testid="login-notice">
            {notice}
          </p>
        )}

        <form onSubmit={submit} className="space-y-3" data-testid="auth-form">
          {mode === 'register' && (
            <div className={inputWrap} style={{ borderColor: 'rgba(0,0,0,0.2)' }}>
              <UserIcon size={16} style={{ color: 'var(--olive)' }} />
              <input className={inputCls} placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name" />
            </div>
          )}
          <div className={inputWrap} style={{ borderColor: 'rgba(0,0,0,0.2)' }}>
            <Mail size={16} style={{ color: 'var(--olive)' }} />
            <input type="email" required className={inputCls} placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="auth-email" />
          </div>
          {mode === 'register' && <EmailHint value={email} testid="register-email-hint" onValidity={setEmailOk} />}
          {mode !== 'forgot' && (
            <div className={inputWrap} style={{ borderColor: 'rgba(0,0,0,0.2)' }}>
              <Lock size={16} style={{ color: 'var(--olive)' }} />
              <input type={pwType} required minLength={mode === 'register' ? 8 : undefined} className={inputCls}
                placeholder={mode === 'register' ? 'Password (min 8 characters)' : 'Password'}
                value={password} onChange={(e) => setPassword(e.target.value)} data-testid="auth-password" />
              {pwEye}
            </div>
          )}

          <button type="submit" disabled={busy || (mode === 'register' && !emailOk)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded font-semibold transition-colors"
            style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: busy ? 0.7 : 1 }}
            data-testid="auth-submit">
            {busy && <Loader2 size={16} className="animate-spin" />}
            {mode === 'login' ? 'Sign in' : mode === 'register' ? 'Create account' : 'Send reset link'}
          </button>
        </form>

        {mode === 'login' && (
          <div className="text-right mt-2">
            <button className="text-sm underline" style={{ color: 'var(--navy)' }} onClick={() => { setMode('forgot'); reset(); }} data-testid="forgot-link">
              Forgot password?
            </button>
          </div>
        )}

        {mode !== 'forgot' && (
          <>
            <div className="flex items-center gap-3 my-5">
              <span className="flex-1 h-px" style={{ background: 'rgba(0,0,0,0.1)' }} />
              <span className="text-xs" style={{ color: 'var(--olive)' }}>OR</span>
              <span className="flex-1 h-px" style={{ background: 'rgba(0,0,0,0.1)' }} />
            </div>
            <button
              onClick={loginWithGoogle}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded border bg-white transition-colors hover:bg-gray-50"
              style={{ borderColor: 'rgba(0,0,0,0.2)', color: '#3c4043', fontWeight: 600 }}
              data-testid="google-login-btn"
            >
              <GoogleIcon /> Continue with Google
            </button>
          </>
        )}

        <div className="mt-6 pt-5 border-t text-sm text-center" style={{ borderColor: 'rgba(0,0,0,0.1)', color: 'var(--olive)' }}>
          {mode === 'login' && (
            <p>New here?{' '}
              <button className="underline font-semibold" style={{ color: 'var(--maroon)' }} onClick={() => { setMode('register'); reset(); }} data-testid="switch-register">Create an account</button>
            </p>
          )}
          {mode === 'register' && (
            <p>Already have an account?{' '}
              <button className="underline font-semibold" style={{ color: 'var(--maroon)' }} onClick={() => { setMode('login'); reset(); }} data-testid="switch-login">Sign in</button>
            </p>
          )}
          {mode === 'forgot' && (
            <button className="underline font-semibold" style={{ color: 'var(--maroon)' }} onClick={() => { setMode('login'); reset(); }} data-testid="back-to-login">Back to sign in</button>
          )}
        </div>

        {mode === 'register' && (
          <p className="flex items-start gap-2 mt-4 text-sm" style={{ color: 'var(--olive)' }}>
            <ShieldCheck size={16} className="mt-0.5 flex-shrink-0" style={{ color: 'var(--navy)' }} />
            <span>New members are granted <strong>Contact</strong> level by default. An administrator can raise your membership class to unlock further pages.</span>
          </p>
        )}
      </div>
    </div>
  );
}
